import { Button } from '@helix-ds/react'
import { ArrowRight } from 'flowbite-react-icons/outline'
import type { ReactNode } from 'react'
import { Modal, SaveIndicator } from '@/components/common/Modal'

const CLINICAL_INDICATIONS_TITLE = 'Clinical Indications for Testing'

interface ClinicalIndicationsModalProps {
  isOpen: boolean
  /** Who the order is for (patient name and age). */
  subtitle?: string
  /** Escape, the close button and the backdrop all save and close, as does Done. */
  onSaveAndClose: () => void
  /** Restores the section to how it was when the modal opened, then closes. */
  onDiscard: () => void
  lastSaveTime: number | null
  isSaving?: boolean
  /** The latest edit did not reach the server, so the footer must not claim it is stored. */
  saveFailed?: boolean
  /** The next section's "Continue to …" step; without it the primary action is Done. */
  continueLabel?: string
  onContinue?: () => void
  children: ReactNode
}

export function ClinicalIndicationsModal({
  isOpen,
  subtitle,
  onSaveAndClose,
  onDiscard,
  lastSaveTime,
  isSaving = false,
  saveFailed = false,
  continueLabel,
  onContinue,
  children,
}: ClinicalIndicationsModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onSaveAndClose}
      title={CLINICAL_INDICATIONS_TITLE}
      subtitle={subtitle || undefined}
      size="xl"
      layout="padded"
      footer={
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="flex flex-1 items-center gap-2">
            <kbd className="rounded-xs border border-border-base-medium bg-background-tertiary px-[7px] py-[5px] font-sans text-xs font-normal leading-4 text-text-body dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
              esc
            </kbd>
            <span className="text-xs font-normal leading-5 text-text-body-subtle dark:text-gray-500">Save & Close</span>
          </div>
          <SaveIndicator tone="muted" lastSaveTime={lastSaveTime} isSaving={isSaving} saveFailed={saveFailed} />
          <button
            type="button"
            onClick={onDiscard}
            className="rounded-full border-0 bg-white px-4 py-2.5 text-sm font-medium leading-5 text-text-body shadow-xs inset-ring inset-ring-border-base transition-colors hover:bg-background-secondary-medium hover:text-text-heading dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
          >
            Discard Changes
          </button>
          {onContinue ? (
            <Button
              variant="primary"
              size="md"
              type="button"
              onClick={onContinue}
              className="leading-5 shadow-xs focus-visible:ring-brand-600"
            >
              {continueLabel ?? 'Continue'}
              <ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
            </Button>
          ) : (
            <Button
              variant="primary"
              size="md"
              type="button"
              onClick={onSaveAndClose}
              className="leading-5 shadow-xs focus-visible:ring-brand-600"
            >
              Done
            </Button>
          )}
        </div>
      }
    >
      {children}
    </Modal>
  )
}
